import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { 
    FaLayerGroup, 
    FaShieldAlt, 
    FaUsers, 
    FaClipboardCheck, 
    FaBook, 
    FaExternalLinkAlt 
} from 'react-icons/fa';

import '../styles/Platform.css'; // Path to the new CSS file

const Platform = () => {
    // Data for the PhilOS feature cards, now with mockup info
    const platformFeatures = [
        {
            icon: <FaLayerGroup aria-hidden="true" />,
            title: 'Unified Financials',
            description: 'Track donations, grant funding, and expenses in real-time. Your project’s financial health is always clear and current.',
            mockupClass: 'mockup-tablet',
            // Replace with your actual image path
            mockupImage: '/assets/platform-mockups/financial-dashboard.png' 
        },
        {
            icon: <FaUsers aria-hidden="true" />,
            title: 'Donor Management',
            description: 'Nurture donor relationships and see giving trends with actionable insights that help you grow community support.',
            mockupClass: 'mockup-mobile',
             // Replace with your actual image path
            mockupImage: '/assets/platform-mockups/donor-management.png'
        },
        {
            icon: <FaClipboardCheck aria-hidden="true" />,
            title: 'Streamlined Operations',
            description: 'From expense approvals to data visualization, every workflow is simplified, giving you confidence and control.',
            mockupClass: 'mockup-card',
             // Replace with your actual image path
            mockupImage: '/assets/platform-mockups/workflows.png'
        }
    ];

    // Intersection Observer for animations
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) entry.target.classList.add('is-visible');
                });
            }, { threshold: 0.1 }
        );
        const elements = document.querySelectorAll('.platform-reveal');
        elements.forEach(el => observer.observe(el));
        return () => elements.forEach(el => observer.unobserve(el));
    }, []);

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Our Platform | Cause & Impact",
        "url": "https://www.causeandimpact.org/platform",
        "description": "Learn about the modern infrastructure Cause & Impact uses to provide transparent, compliant, and efficient fiscal sponsorship for our projects.",
    };

    return (
        <>
            <Helmet>
                <title>Our Platform | Cause & Impact</title>
                <meta name="description" content="Learn about the modern infrastructure Cause & Impact uses to provide transparent, compliant, and efficient fiscal sponsorship for our projects." />
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>

            <main className="platform-page">

                <section className="platform-hero platform-reveal">
                    <div className="platform-container hero-layout">
                        <div className="hero-content">
                            <h1 id="platform-title">Modern Infrastructure for Modern Missions</h1>
                            <p className="platform-lede">
                                Our fiscal sponsorship is powered by modern infrastructure designed for nonprofits. This allows us to provide real-time transparency, streamlined approvals, and audit-ready compliance, ensuring our projects can focus on their mission.
                            </p>
                        </div>
                        <div className="hero-mockup-container">
                            {/* Replace with your hero image path */}
                            <div className="hero-mockup" style={{ backgroundImage: "url('/assets/Platform/hero-dashboard.png')" }}></div>
                            </div>
                    </div>
                </section>

                <section id="philos" className="platform-section platform-reveal">
                    <div className="platform-container">
                        <h2 className="platform-section-title">PhilOS: The Operating System for Impact</h2>
                        <p className="platform-section-intro">
                            By running on PhilOS, we give our projects the same caliber of tools as well-funded nonprofits.
                        </p>
                        <div className="feature-showcase">
                            {platformFeatures.map((feature, index) => (
                                <div className={`feature-item layout-${index % 2 === 0 ? 'text-left' : 'text-right'}`} key={index}>
                                    <div className="feature-content">
                                        <div className="feature-icon">{feature.icon}</div>
                                        <h3>{feature.title}</h3>
                                        <p>{feature.description}</p>
                                    </div>
                                    <div className="feature-mockup-container">
                                        <div 
                                            className={`feature-mockup ${feature.mockupClass}`}
                                            style={{ backgroundImage: `url('/assets/Platform/financial_Dashbaord.png')` }}
                                        ></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section id="phil-ai" className="platform-section platform-section-alt platform-reveal">
                    <div className="platform-container platform-narrow">
                        <h2 className="platform-section-title">Phil: The AI Compliance Engine</h2>
                        <div className="ai-engine-visual">
                            {/* This is a placeholder for the visual metaphor. You can replace it with an SVG or image. */}
                            <FaShieldAlt aria-hidden="true" />
                        </div>
                        <div className="ai-engine-card">
                            <h3>Built-In Compliance & Risk Reduction</h3>
                            <p>
                                Our operations are supported by Phil, an AI compliance engine. It provides our team with real-time, verifiable guidance on complex IRS and state rules. This reduces risk and helps protect our sponsored projects by ensuring their activities remain audit-ready.
                            </p>
                        </div>
                    </div>
                </section>
                
                <section id="transparency" className="platform-section platform-reveal">
                    <div className="platform-container platform-narrow">
                         <h2 className="platform-section-title">For Transparency</h2>
                         <div className="disclosure-card">
                            <FaBook aria-hidden="true" className="disclosure-icon" />
                            <p>
                                Cause & Impact operates on PhilOS and Phil AI, technologies developed and owned by Just Ask Phil, Inc., a separate public benefit corporation. We license these tools to effectively and efficiently serve our fiscally sponsored projects. Cause & Impact does not sell or receive any commission from these services.
                            </p>
                            <a 
                                href="https://justaskphil.com" // Update this URL if needed
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="external-link-btn"
                            >
                                Learn more about the technology provider <FaExternalLinkAlt aria-hidden="true" />
                            </a>
                         </div>
                    </div>
                </section>

            </main>
        </>
    );
};

export default Platform;